import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import exportToCSV from "../components/exportToCSV";

const apiUrl = process.env.REACT_APP_API_BASE_URL;

export async function loader() {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Response(
      JSON.stringify({
        message: "You must be logged in to access this resource.",
      }),
      { status: 401 }
    );
  }

  const response = await fetch(`${apiUrl}/ct/returns-exchanges`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "X-App-Secret": process.env.REACT_APP_APP_SECRET,
    },
  });

  if (!response.ok) {
    throw new Response(
      JSON.stringify({ message: "Could not fetch returns/exchanges." }),
      { status: response.status }
    );
  }

  const returnsExchanges = await response.json();
  return returnsExchanges;
}

export default function ReturnsExchangesManagement() {
  const returnsExchanges = useLoaderData();
  const navigate = useNavigate();

  const [filters, setFilters] = useState({
    email: "",
    order_number: "",
    request_type: "",
    status: "",
  });
  const [editRowId, setEditRowId] = useState(null);
  const [editableData, setEditableData] = useState({});

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Filter records by checking each filter key; if a filter value is set, the record field must contain it.
  const filteredData = returnsExchanges.filter((item) =>
    Object.entries(filters).every(([key, value]) => {
      if (!value) return true; // Skip filtering if no value is set
      // Ensure both sides are strings for comparison
      return String(item[key]).toLowerCase().includes(value.toLowerCase());
    })
  );

  const handleEditClick = (id, rowData) => {
    setEditRowId(id);
    // For this example, we allow editing only the status.
    // You could add more fields if your update endpoint supports them.
    setEditableData({
      status: rowData.status,
    });
  };

  const handleSaveClick = async (id) => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        alert("You must be logged in to save changes.");
        navigate("/login");
        return;
      }

      // Using PUT and a URL parameter (if you set up your route that way)
      const response = await fetch(
        `${apiUrl}/ct/returns-exchanges/update/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-App-Secret": process.env.REACT_APP_APP_SECRET,
          },
          body: JSON.stringify({
            status: editableData.status,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update the return/exchange request");
      }

      // In this example, we assume the updated record is returned in the response.
      const updatedItem = await response.json();

      alert(updatedItem.message);

      // Optionally update the UI if you keep local state in sync.
      // (In this example, we’re not using a setter for the entire list, so you might want to reload or manage state differently.)
      setEditRowId(null);
      navigate(".", { replace: true });
    } catch (err) {
      console.error("Error updating return/exchange request:", err);
      alert("An error occurred while saving the return/exchange request.");
    }
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditableData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="returns-exchanges-container">
      <h1>Returns &amp; Exchanges Management</h1>

      <div className="filters">
        <label>
          Email:
          <input
            type="text"
            name="email"
            value={filters.email}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Order Number:
          <input
            type="text"
            name="order_number"
            value={filters.order_number}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Request Type:
          <input
            type="text"
            name="request_type"
            value={filters.request_type}
            onChange={handleFilterChange}
          />
        </label>
        <label>
          Status:
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="validated">Validated</option>
            <option value="processed">Processed</option>
            <option value="completed">Completed</option>
          </select>
        </label>
      </div>
      <div className="export-button">
        <button
          onClick={() => exportToCSV(filteredData, "returns_exchanges.csv")}
          className="export-btn"
        >
          Export CSV
        </button>
      </div>

      <table className="management-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Email</th>
            <th>Order Number</th>
            <th>Request Type</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Items To Return</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredData.map((item) => (
            <tr key={item.id}>
              {editRowId === item.id ? (
                <>
                  <td>{item.id}</td>
                  <td>{item.email}</td>
                  <td>{item.order_number}</td>
                  <td>{item.request_type}</td>
                  <td>
                    <select
                      name="status"
                      value={editableData.status}
                      onChange={handleEditChange}
                    >
                      <option value="pending">Pending</option>
                      <option value="validated">Validated</option>
                      <option value="processed">Processed</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                  <td>{new Date(item.created_at).toLocaleString()}</td>
                  <td>
                    <ul style={{ margin: 0, paddingLeft: "16px" }}>
                      {(() => {
                        try {
                          const items =
                            typeof item.items_to_return === "string"
                              ? JSON.parse(item.items_to_return)
                              : item.items_to_return;

                          return items.map((i, idx) => (
                            <li key={idx}>
                              {i.name} – Size {i.size} – Qty {i.quantity}
                            </li>
                          ));
                        } catch (err) {
                          return <li style={{ color: "red" }}>Invalid data</li>;
                        }
                      })()}
                    </ul>
                  </td>
                  <td>
                    <button
                      className="save-btn"
                      onClick={() => handleSaveClick(item.id)}
                    >
                      Save
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td>{item.id}</td>
                  <td>{item.email}</td>
                  <td>{item.order_number}</td>
                  <td>{item.request_type}</td>
                  <td>{item.status}</td>
                  <td>{new Date(item.created_at).toLocaleString()}</td>
                  <td>
                    <ul style={{ margin: 0, paddingLeft: "16px" }}>
                      {(() => {
                        try {
                          const items =
                            typeof item.items_to_return === "string"
                              ? JSON.parse(item.items_to_return)
                              : item.items_to_return;

                          return items.map((i, idx) => (
                            <li key={idx}>
                              {i.name} – Size {i.size} – Qty {i.quantity}
                            </li>
                          ));
                        } catch (err) {
                          return <li style={{ color: "red" }}>Invalid data</li>;
                        }
                      })()}
                    </ul>
                  </td>
                  <td>
                    <button
                      className="edit-btn"
                      onClick={() => handleEditClick(item.id, item)}
                    >
                      Edit
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
